import React, { useState, useEffect, useRef } from 'react';
import { apiService } from '../services/api';
import { CheckCircle2, Clock, XCircle, RefreshCw, AlertCircle, Timer, ShieldCheck, Calendar, Layers, Download, Lock, Unlock } from 'lucide-react';
import { API_BASE_URL } from '../config';

const PollingStatusMonitor = () => {
  const [pollingData, setPollingData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [validationData, setValidationData] = useState(null);
  const [validationLoading, setValidationLoading] = useState(true);
  const [versions, setVersions] = useState({});
  const [locks, setLocks] = useState({});
  const isMounted = useRef(true);

  // Model version status per symbol (keyed lowercase). Non-fatal if it fails.
  const fetchVersions = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/analytics/reports/versions/`);
      if (!res || !res.ok) return;            // endpoint missing/failed -> show nothing
      const data = await res.json();
      const arr = Array.isArray(data && data.symbols) ? data.symbols : [];
      const map = {};
      arr.forEach((s) => { if (s && s.symbol) map[s.symbol] = s; });
      if (isMounted.current) setVersions(map);
    } catch (e) {
      // ignore — the Model line just won't render, page is unaffected
    }
  };

  // Per-symbol immutability lock (frozen). Keyed uppercase to match polling rows.
  // MV owns the flag; this just reflects it live. Non-fatal if it fails.
  const fetchLocks = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_BASE_URL}/analytics/symbols/?page_size=100&is_active=all`, {
        headers: token ? { Authorization: `Basic ${token}` } : {}
      });
      if (!res || !res.ok) return;            // endpoint missing/failed -> show nothing
      const data = await res.json();
      const list = Array.isArray(data) ? data : (Array.isArray(data && data.results) ? data.results : []);
      const map = {};
      list.forEach((s) => { if (s && s.name) map[s.name.toUpperCase()] = !!s.frozen; });
      if (isMounted.current) setLocks(map);
    } catch (e) {
      // ignore — the lock icon just won't render, page is unaffected
    }
  };

  const fetchPollingStatus = async () => {
    try {
      if (isMounted.current) setError(null);
      const response = await apiService.getPollingStatus();
      if (isMounted.current) {
        setPollingData(response.data);
        setLoading(false);
      }
    } catch (err) {
      if (isMounted.current) {
        setError(`Failed to load polling status: ${err.response?.data?.detail || err.message}`);
        setLoading(false);
      }
    }
  };

  const fetchValidation = async (force = false) => {
    try {
      const params = force ? { force: '1' } : {};
      const response = await apiService.getClosePriceValidation(params);
      if (isMounted.current) {
        setValidationData(response.data);
        setValidationLoading(false);
      }
    } catch {
      if (isMounted.current) setValidationLoading(false);
    }
  };

  const handleRefresh = () => {
    setValidationData(null);
    setValidationLoading(true);
    fetchPollingStatus();
    fetchValidation(true);
    fetchVersions();
    fetchLocks();
  };

  useEffect(() => {
    isMounted.current = true;
    fetchPollingStatus();
    fetchValidation();
    fetchVersions();
    fetchLocks();

    // Only auto-refresh polling status, not validation
    const refreshInterval = setInterval(() => { fetchPollingStatus(); fetchLocks(); }, 30000);
    return () => {
      isMounted.current = false;
      clearInterval(refreshInterval);
    };
  }, []);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'ready':
        return <CheckCircle2 size={16} className="text-green-400" />;
      case 'processing':
        return <Clock size={16} className="text-yellow-400 animate-pulse" />;
      case 'failed':
        return <XCircle size={16} className="text-red-400" />;
      default:
        return <AlertCircle size={16} className="text-gray-400" />;
    }
  };

  const getStatusBadge = (status) => {
    const baseClasses = "px-2 py-0.5 rounded-full text-[10px] font-medium inline-flex items-center gap-1";
    switch (status) {
      case 'ready':
        return `${baseClasses} bg-green-500/20 text-green-400 border border-green-500/30`;
      case 'processing':
        return `${baseClasses} bg-yellow-500/20 text-yellow-400 border border-yellow-500/30`;
      case 'failed':
        return `${baseClasses} bg-red-500/20 text-red-400 border border-red-500/30`;
      default:
        return `${baseClasses} bg-gray-500/20 text-gray-400 border border-gray-500/30`;
    }
  };

  const formatCloseTimeIST = (timeStr) => {
    if (!timeStr) return null;
    const [h, m] = timeStr.split(':').map(Number);
    const period = h >= 12 ? 'PM' : 'AM';
    const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${hour12}:${m.toString().padStart(2, '0')} ${period}`;
  };

  const getSourceLabel = (source) => {
    if (!source) return null;
    const names = {
      'eodhd': 'EODHD',
      'yahoo': 'Yahoo',
      'nse': 'NSE',
      'bse': 'BSE',
    };
    return names[source.toLowerCase()] || source.toUpperCase();
  };

  const formatShortDate = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getMarketOpenBadge = (marketOpen, validationResult) => {
    if (marketOpen === null || marketOpen === undefined) return null;
    if (marketOpen) {
      return (
        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-500/20 text-green-400 border border-green-500/30">
          Open
        </span>
      );
    }

    // Market is closed — show context from validation result
    if (validationResult) {
      const { market_status, holiday_name, date } = validationResult;

      // Holiday
      if (holiday_name || market_status === 'holiday') {
        return (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-500/20 text-blue-300 border border-blue-500/30"
                title={holiday_name ? `Holiday: ${holiday_name}` : 'Market holiday'}>
            <Calendar size={10} />
            {holiday_name || 'Holiday'}
          </span>
        );
      }

      // Weekend
      if (market_status === 'weekend') {
        return (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-500/20 text-blue-300 border border-blue-500/30">
            <Calendar size={10} />
            Weekend
          </span>
        );
      }
    }

    // Regular closed
    return (
      <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-dark-500/20 text-dark-300 border border-dark-500/30">
        Closed
      </span>
    );
  };

  const getValidationBadges = (result) => {
    if (!result) return null;

    // Market open or not yet validated — show nothing
    if (result.status === 'pending' || result.status === 'error') {
      return null;
    }

    // Settling — market just closed, too early to verify
    if (result.settling) {
      return [(
        <span
          key="settling"
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border cursor-help bg-amber-500/15 text-amber-400 border-amber-400/40 animate-pulse"
          title={result.note || 'Market closed less than 1 hour ago — close prices may still be settling'}
        >
          <Timer size={10} />
          Settling
        </span>
      )];
    }

    // Close Verified — at least one source confirms our close
    if (result.status === 'passed') {
      const sourceLabel = result.verified_by ? result.verified_by.join(', ') : '';
      const dateLabel = result.date ? formatShortDate(result.date) : '';
      return [(
        <span
          key="verified"
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border cursor-help bg-green-500/15 text-green-400 border-green-400/40"
          title={`Close price verified correct${dateLabel ? ` for ${dateLabel}` : ''}\nDB: ${result.db_close?.toFixed(2)}`}
        >
          <ShieldCheck size={10} />
          {dateLabel && <span className="text-green-400/70">{dateLabel}</span>}
          Verified{sourceLabel ? ` (${sourceLabel})` : ''}
        </span>
      )];
    }

    // Close Mismatch — no source confirms our close
    const mismatchDateLabel = result.date ? formatShortDate(result.date) : '';
    return [(
      <span
        key="mismatch"
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border cursor-help bg-red-500/15 text-red-400 border-red-400/40"
        title={`Close price mismatch${mismatchDateLabel ? ` for ${mismatchDateLabel}` : ''} — does not match any external source\nDB: ${result.db_close?.toFixed(2)}`}
      >
        <XCircle size={10} />
        {mismatchDateLabel && <span className="text-red-400/70">{mismatchDateLabel}</span>}
        Mismatch
      </span>
    )];
  };

  const buildValidationTooltip = (result) => {
    if (!result) return '';
    const lines = [];
    if (result.date) lines.push(`Date: ${result.date}`);
    if (result.status === 'pending') {
      if (result.message) lines.push(result.message);
      if (result.retry_after_ist) lines.push(`Retry after: ${result.retry_after_ist} IST`);
      return lines.join('\n');
    }
    if (result.db_close != null) lines.push(`DB: ${result.db_close.toFixed(2)}`);
    if (result.sources) {
      for (const [src, info] of Object.entries(result.sources)) {
        const label = src.toUpperCase();
        if (info.close != null) {
          lines.push(`${label}: ${info.close.toFixed(2)} ${info.match ? '(match)' : '(MISMATCH)'}`);
        } else if (info.error) {
          lines.push(`${label}: ${info.error}`);
        } else {
          lines.push(`${label}: N/A`);
        }
      }
    }
    return lines.join('\n');
  };

  const getValidationForSymbol = (marketKey, symbolName) => {
    if (!validationData || !validationData[marketKey]) return null;
    return validationData[marketKey].find(v => v.symbol === symbolName);
  };

  // Model-version line for a symbol card. Three states:
  //   - has a previous different version -> "Model vN · changed" + download previous
  //   - only v1 (no previous)            -> "Model v1 · no previous version"
  //   - not tracked yet                  -> render nothing
  const renderModelLine = (symbolName) => {
    const v = versions[(symbolName || '').toLowerCase()];
    if (!v || !v.head_version) return null;
    const hasPrev = v.has_previous && v.last_different_version;
    return (
      <div className="flex items-center gap-2 mt-1.5 ml-6 pt-1.5 border-t border-dark-700/50 text-[11px] text-dark-400">
        <Layers size={10} className={hasPrev ? 'text-amber-400' : 'text-dark-500'} />
        <span className="text-dark-300">Model <span className="font-mono">v{v.head_version}</span></span>
        {hasPrev ? (
          <>
            <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-500/10 text-amber-400">
              changed
            </span>
            <a
              href={`${API_BASE_URL}/analytics/reports/versions/download/?symbol=${(symbolName || '').toLowerCase()}`}
              target="_blank"
              rel="noreferrer"
              className="ml-auto inline-flex items-center gap-1 text-primary-400 hover:text-primary-300"
            >
              <Download size={10} /> Download previous (v{v.last_different_version})
            </a>
          </>
        ) : (
          <span className="text-dark-500">no previous version</span>
        )}
      </div>
    );
  };

  const formatUpdateTime = (isoTimestamp) => {
    if (!isoTimestamp) return 'N/A';
    const dt = new Date(isoTimestamp);
    const dateStr = dt.toLocaleDateString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: 'numeric',
      month: 'short',
    });
    const timeStr = dt.toLocaleTimeString('en-IN', {
      timeZone: 'Asia/Kolkata',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    return `${timeStr}, ${dateStr} IST`;
  };

  const getMarketDisplayName = (marketKey) => {
    const marketNames = {
      'india_stocks_indexes': 'India',
      'us_stocks_indexes': 'US',
      'europe_stocks_indexes': 'Europe',
      'asia_pacific': 'Asia Pacific',
      'international_stocks_indexes': 'International',
      'crypto': 'Crypto',
      'precious_metals': 'Metals'
    };
    return marketNames[marketKey] || marketKey;
  };

  const getMarketOrder = () => {
    return ['india_stocks_indexes', 'us_stocks_indexes', 'europe_stocks_indexes', 'asia_pacific', 'crypto'];
  };

  const getOverallStatus = (data) => {
    let ready = 0, processing = 0, failed = 0, total = 0, marketsOpen = 0;

    // Only count markets that are displayed on the dashboard
    for (const key of marketOrder) {
      const market = data[key];
      if (!market) continue;
      market.forEach(item => {
        total++;
        if (item.status === 'ready') ready++;
        else if (item.status === 'processing') processing++;
        else if (item.status === 'failed') failed++;
        if (item.market_open === true) marketsOpen++;
      });
    }

    return { ready, processing, failed, total, marketsOpen };
  };

  const marketOrder = getMarketOrder();
  const overallStats = Object.keys(pollingData).length > 0 ? getOverallStatus(pollingData) : null;
  const validationSummary = validationData?.summary;

  // Group settling symbols by market and count totals
  const settlingByMarket = {};
  let settlingCount = 0;
  if (validationData) {
    for (const group of marketOrder) {
      const items = validationData[group];
      if (!items) continue;
      const settling = items.filter(item => item.settling).map(item => item.symbol);
      if (settling.length > 0) {
        settlingByMarket[group] = settling;
        settlingCount += settling.length;
      }
    }
  }

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-dark-800 to-dark-900 rounded-xl p-6 border border-dark-600/50 shadow-xl">
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="animate-spin text-blue-400" size={24} />
          <span className="ml-3 text-dark-300">Loading polling status...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gradient-to-br from-dark-800 to-dark-900 rounded-xl p-6 border border-red-500/30 shadow-xl">
        <div className="flex items-center text-red-400">
          <XCircle size={20} />
          <span className="ml-3">{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-dark-800 to-dark-900 rounded-xl p-6 border border-dark-600/50 shadow-xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <RefreshCw size={20} className="text-blue-400" />
            Data Polling Status
          </h2>
          <p className="text-sm text-dark-300 mt-1">
            Real-time monitoring of all market data updates
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={validationLoading}
          className="flex items-center gap-1.5 px-3 py-1.5 hover:bg-dark-700 rounded-lg transition-colors disabled:opacity-50 text-xs text-dark-400"
          title="Refresh polling status and verify close prices"
        >
          <RefreshCw size={14} className={`${validationLoading ? 'animate-spin text-blue-400' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Overall Status Summary */}
      {overallStats && (
        <div className="grid grid-cols-6 gap-4 mb-6">
          <div className="bg-dark-700/50 rounded-lg p-4 border border-dark-600/30">
            <div className="text-2xl font-bold text-white">{overallStats.total}</div>
            <div className="text-xs text-dark-400 mt-1">Total Indexes</div>
          </div>
          <div className="bg-green-500/10 rounded-lg p-4 border border-green-500/20">
            <div className="text-2xl font-bold text-green-400">{overallStats.ready}</div>
            <div className="text-xs text-green-400/70 mt-1">Ready</div>
          </div>
          <div className="bg-yellow-500/10 rounded-lg p-4 border border-yellow-500/20">
            <div className="text-2xl font-bold text-yellow-400">{overallStats.processing}</div>
            <div className="text-xs text-yellow-400/70 mt-1">Processing</div>
          </div>
          <div className="bg-red-500/10 rounded-lg p-4 border border-red-500/20">
            <div className="text-2xl font-bold text-red-400">{overallStats.failed}</div>
            <div className="text-xs text-red-400/70 mt-1">Failed</div>
          </div>
          <div className="bg-blue-500/10 rounded-lg p-4 border border-blue-500/20">
            <div className="text-2xl font-bold text-blue-400">{overallStats.marketsOpen}</div>
            <div className="text-xs text-blue-400/70 mt-1">Markets Open</div>
          </div>
          <div className="bg-purple-500/10 rounded-lg p-4 border border-purple-500/20">
            <div className="text-2xl font-bold text-purple-400">
              {validationSummary ? `${validationSummary.passed}/${validationSummary.total - validationSummary.pending - settlingCount}` : '—'}
            </div>
            <div className="text-xs text-purple-400/70 mt-1">
              Close Verified
              {settlingCount > 0 && (
                <span className="text-amber-400"> ({settlingCount} settling)</span>
              )}
              {validationSummary && validationSummary.failed - settlingCount > 0 && (
                <span className="text-red-400"> ({validationSummary.failed - settlingCount} failed)</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Market Groups */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {marketOrder.filter(marketKey => pollingData[marketKey] && pollingData[marketKey].length > 0).map((marketKey) => (
          <div key={marketKey} className="bg-dark-700/30 rounded-lg p-4 border border-dark-600/20">
            <h3 className="text-sm font-semibold text-blue-400 mb-3 uppercase tracking-wider">
              {getMarketDisplayName(marketKey)}
            </h3>
            {settlingByMarket[marketKey] && (
              <div className="flex items-center gap-1.5 mb-3 px-3 py-1.5 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <AlertCircle size={12} className="text-yellow-400 flex-shrink-0" />
                <span className="text-[11px] text-yellow-300">
                  Settling — close prices may update within 1 hour of market close
                </span>
              </div>
            )}
            <div className="space-y-2">
              {pollingData[marketKey].map((item, index) => {
                const validationResult = getValidationForSymbol(marketKey, item.symbol);
                return (
                  <div
                    key={index}
                    className="p-3 bg-dark-800/50 rounded-lg hover:bg-dark-800 transition-colors"
                  >
                    {/* Row 1: Symbol, value, status (only if not ready), time ago */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(item.status)}
                        <span className="text-white font-medium text-sm">{item.symbol}</span>
                        {locks[item.symbol] !== undefined && (
                          <span
                            className="inline-flex"
                            title={locks[item.symbol] ? 'Locked — history immutable' : 'Unlocked — recompute allowed'}
                          >
                            {locks[item.symbol]
                              ? <Lock size={12} className="text-amber-400" />
                              : <Unlock size={12} className="text-dark-500" />}
                          </span>
                        )}
                        {item.last_close != null && (
                          <span className="text-dark-300 text-sm font-mono">
                            {item.last_close.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        )}
                        {item.status !== 'ready' && (
                          <span className={getStatusBadge(item.status)}>
                            {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                          </span>
                        )}
                      </div>
                      <span className="text-[11px] text-dark-400 text-right">
                        {formatUpdateTime(item.last_updated_at)}
                      </span>
                    </div>
                    {/* Row 2: Close time, market open/closed, validation */}
                    <div className="flex items-center gap-3 mt-1.5 ml-6 text-[11px] text-dark-400">
                      {item.market_close_ist && (
                        <span className="flex items-center gap-1">
                          <Timer size={10} />
                          Close: <span className="text-dark-300">{formatCloseTimeIST(item.market_close_ist)} IST</span>
                        </span>
                      )}
                      {item.market_open !== null && item.market_open !== undefined && (
                        <>
                          {item.market_close_ist && <span className="text-dark-600">|</span>}
                          {getMarketOpenBadge(item.market_open, validationResult)}
                        </>
                      )}
                      {item.last_report_at && (
                        <>
                          <span className="text-dark-600">|</span>
                          <span className="flex items-center gap-1">
                            Report: <span className="text-dark-300">{item.last_report_at}</span>
                          </span>
                        </>
                      )}
                      {validationResult && getValidationBadges(validationResult) ? (
                        <>
                          <span className="text-dark-600">|</span>
                          {getValidationBadges(validationResult)}
                        </>
                      ) : (
                        !validationResult && validationLoading && item.market_open === false && (
                          <>
                            <span className="text-dark-600">|</span>
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium text-dark-400 animate-pulse">
                              <ShieldCheck size={10} />
                              Verifying close...
                            </span>
                          </>
                        )
                      )}
                    </div>
                    {/* Row 3: Model version status */}
                    {renderModelLine(item.symbol)}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PollingStatusMonitor;
